import React, { useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { motion, AnimatePresence } from 'motion/react'
import { ArrowUpRight } from '@phosphor-icons/react'
import { classNames } from 'src/lib/utils/helpers'
import useNavigation from 'src/lib/hooks/useNavigation'
import { NavigationItem } from '../PrimaryTabs'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@shopflo/ui'
import SubMenuExpanded from './SubMenuExpanded'
import { isCurrentlySelected } from 'src/lib/utils/path-matching'
import { useLocale } from 'src/lib/hooks/useLocal'
import NavNewBadge from '../../NavNewBadge'
import { dismissNavShimmer, isNavShimmerDismissed } from '../../nav-new-badges'

interface NavigationMenuItemProps {
  menuItem: NavigationItem
  isExpanded: boolean
  showQueryPulse?: boolean
}

const NavigationMenuItem = ({ menuItem, isExpanded, showQueryPulse = false }: NavigationMenuItemProps) => {
  const router = useRouter()
  const {
    actions: { navigateWithSubmenuHandling, setOpenHoverCardId },
    getters: { sidebarOpen, isTablet, openHoverCardId }
  } = useNavigation()
  const { t } = useLocale()

  const hasSubMenuItems = Boolean(menuItem.children && menuItem.children.length > 0)
  const menuItemRef = useRef<HTMLDivElement>(null)

  const isSelected = isCurrentlySelected(router.asPath, menuItem, isTablet)
  const isHoverCardOpen = openHoverCardId === menuItem.id

  const [shimmerDismissed, setShimmerDismissed] = useState<boolean>(
    () => (menuItem.showNewBadge ? isNavShimmerDismissed(menuItem.id) : true)
  )
  const prevSidebarOpenRef = useRef(sidebarOpen)
  const animateSidebarLabelsRef = useRef(false)

  if (prevSidebarOpenRef.current !== sidebarOpen) {
    animateSidebarLabelsRef.current = true
    prevSidebarOpenRef.current = sidebarOpen
  }

  const handleNavigationClick = () => {
    if (menuItem.showNewBadge && !shimmerDismissed) {
      dismissNavShimmer(menuItem.id)
      setShimmerDismissed(true)
    }
    setOpenHoverCardId(null)
    navigateWithSubmenuHandling(menuItem.path)
  }

  const handleSubMenuNavigation = (path: string) => {
    setOpenHoverCardId(null)
    navigateWithSubmenuHandling(path)
  }

  const handleHoverCardOpenChange = (open: boolean) => {
    setOpenHoverCardId(open ? menuItem.id : null)
  }

  const renderButtonContent = () => (
    <motion.button
      onClick={handleNavigationClick}
      disabled={menuItem.disabled}
      id={menuItem.id}
      className={classNames(
        'relative flex w-full items-center rounded-md px-1 py-1 hover:bg-black/5 h-[28px] justify-start overflow-hidden',
        isSelected ? 'bg-white shadow-[0_0_0_0.5px_rgba(54,65,83,0.06),0_0.5px_1px_0_rgba(54,65,83,0.20)]' : '',
        menuItem.disabled ? 'opacity-40 cursor-not-allowed' : '',
        menuItem.showNewBadge && !shimmerDismissed ? 'nav-shimmer' : ''
      )}
    >
      <div className='grid min-w-[28px] justify-center'>
        <menuItem.icon size={18} weight='duotone' color='#4A5565' />
      </div>
      <AnimatePresence mode='wait'>
        {sidebarOpen && (
          <>
            <motion.span
              initial={animateSidebarLabelsRef.current ? { opacity: 1, width: 0 } : false}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className='text-text-sm-medium flex min-w-0 flex-1 items-center gap-1.5 px-1 text-left'
            >
              <span className='truncate'>{menuItem.title}</span>
              {showQueryPulse ? (
                <span className='relative inline-flex size-[6px] shrink-0 items-center justify-center' aria-hidden='true'>
                  <span className='absolute inset-0 rounded-full bg-emerald-400 opacity-75 animate-ping' />
                  <span className='relative size-[6px] rounded-full bg-emerald-500' />
                </span>
              ) : null}
            </motion.span>
            {menuItem.showNewBadge && (
              <div className='flex shrink-0 items-center px-0.5'>
                <NavNewBadge />
              </div>
            )}
          </>
        )}
      </AnimatePresence>
      {!sidebarOpen && showQueryPulse && (
        <span
          className='absolute right-1 top-1/2 -translate-y-[calc(50%+4px)] inline-flex size-[6px] shrink-0 items-center justify-center'
          aria-hidden='true'
        >
          <span className='absolute inset-0 rounded-full bg-emerald-400 opacity-75 animate-ping' />
          <span className='relative size-[6px] rounded-full bg-emerald-500' />
        </span>
      )}
    </motion.button>
  )

  const renderHoverCardContent = () => (
    <div>
      <div className='flex flex-col'>
        <button className='flex items-center max-h-8 w-full text-left gap-2 text-text-sm-regular text-gray-300 px-2 py-1.5 rounded-md cursor-default'>
          <ArrowUpRight size={14} className='text-white/60 text-text-xs-medium' weight='bold' />
          <span>{t('navigation.jump_to')}</span>
        </button>
        <button
          onClick={handleNavigationClick}
          className=' w-full text-left text-text-sm-medium text-white hover:text-white px-2 py-1.5 rounded-md hover:bg-white/5'
          id={menuItem.id}
        >
          {menuItem.title}
        </button>
        {hasSubMenuItems &&
          menuItem.children!.map(subMenuItem => (
            <button
              key={subMenuItem.title}
              onClick={() => {
                handleSubMenuNavigation(subMenuItem.path)
              }}
              id={subMenuItem.id}
              className='block w-full text-left text-text-sm-medium text-white hover:text-white px-2 py-1.5 rounded-md hover:bg-white/5'
            >
              {subMenuItem.title}
            </button>
          ))}
      </div>
    </div>
  )

  const renderCollapsedView = () => (
    <HoverCard openDelay={300} closeDelay={300} open={isHoverCardOpen} onOpenChange={handleHoverCardOpenChange}>
      <HoverCardTrigger asChild>{renderButtonContent()}</HoverCardTrigger>
      <HoverCardContent
        side='right'
        align='start'
        className='bg-zinc-800 text-white rounded-xl p-1.5 shadow-lg min-w-[220px] border-0'
        asChild
      >
        {renderHoverCardContent()}
      </HoverCardContent>
    </HoverCard>
  )

  const renderExpandedView = () => (
    <>
      {renderButtonContent()}
      {hasSubMenuItems && <SubMenuExpanded menuItem={menuItem} isExpanded={isExpanded} />}
    </>
  )

  return (
    <div ref={menuItemRef} className='w-full relative group'>
      {!sidebarOpen ? renderCollapsedView() : renderExpandedView()}
    </div>
  )
}

export default NavigationMenuItem
